import { UtilArrayS } from '../../../../Util/Array/S/Function/module/UtilArrayS.js';
import { SVG_EL_ITEM } from '../../S/Constant/constants.js';
import { DfImageEditorF } from './module/DfImageEditorF.js';
import { handleSvgImageFile, setWidthAndHeightIntoCm } from './tool.file.js';

export async function getSvgPrintableImageWithCuttingPath({
  tab_el,
  paper_scope,
  cutting_path,
  file_info: { projection_id, up_id, face_name, total_qty },
  is_fill_color,
}) {
  const TRANSPARENT = new paper_scope.Color(0, 0, 0, 0);

  const printable_raster = DfImageEditorF.getPrintableRaster({ paper_scope });

  let cutting_line;
  if (is_fill_color) {
    const paths = DfImageEditorF.getAllPaths({ item: cutting_path });

    if (!UtilArrayS.isArrayOk(paths)) {
      throw new Error(`칼선 path가 존재하지 않습니다.`);
    }

    // Path 경로 트리 생성
    const pathTree = buildPathTree(paths);

    // Path 경로 트리를 이용하여 중첩 구조를 고려한 새로운 경로 생성 (중첩 구조를 고려하여 색깔 채우기 처리)
    const cutting_paths = processPathTree(pathTree, printable_raster, paper_scope, TRANSPARENT);

    cutting_line = new paper_scope.Group(cutting_paths);
  } else {
    cutting_line = cutting_path.clone();
  }
  cutting_line.strokeColor = TRANSPARENT;
  cutting_line.name = '칼선';

  const stroke = DfImageEditorF.getRStrokeRaster({ tab_el });

  let output_raster;
  if (stroke) {
    output_raster = stroke.clone();
    output_raster.drawImage(
      printable_raster.image,
      printable_raster.bounds.topLeft.subtract(output_raster.bounds.topLeft),
    );
  } else {
    output_raster = printable_raster.clone();
  }
  output_raster.name = SVG_EL_ITEM.raster;

  /* 1. 시안 정보 QR 코드, 주문 정보, 디자인 크기 등
   *     - 추가하지 않는 것으로 스펙 변경되어 commented out
   * */
  // const info_group = await DfImageEditorF.getExportFileInfoGroup({
  //   tab_el,
  //   projection_id,
  //   up_id,
  //   face_name,
  //   total_qty,
  // });
  /* --- 여기까지 기존 project 내의 item 들을 가지고 파일 추출을 위한 post-processing 진행 */
  /* project 깨끗하게 정리한 다음에 export layer 한 층을 추가해서 여기에 추출할 정리된 객체들을 담음 */
  paper_scope.project.clear();
  const export_layer = new paper_scope.Layer({ insert: true });

  /* 1. path 에 색상을 채우는 경우 (ex. FLEX) "칼선"
     - 디자인 이미지는 필요 없기 떄문에 추출하지 않고 칼선만 추출함.
   * 2. 일반적인 경우에는 raster 이미지와 칼선을 함께 추출 "디자인 칼선"
  */
  if (is_fill_color) {
    export_layer.addChild(cutting_line);
  } else {
    const raster_and_path_group = new paper_scope.Group();
    raster_and_path_group.addChild(output_raster);
    raster_and_path_group.addChild(cutting_line);
    raster_and_path_group.name = '디자인 칼선';
    export_layer.addChild(raster_and_path_group);
  }

  // export_layer.addChild(info_group);
  export_layer.visible = true;
  export_layer.position = new paper_scope.Point(0, 0).subtract(export_layer.bounds.topLeft);

  // const export_svg = DfImageEditorF.sanitizeSvgEl({
  //   svg_el: paper_scope.project.exportSVG({
  //     bounds: 'content',
  //     embedImages: true,
  //   }),
  // });
  const export_svg = paper_scope.project.exportSVG({
    bounds: 'content',
    embedImages: true,
  });

  setWidthAndHeightIntoCm({ svg_el: export_svg });

  return {
    svg_string: new XMLSerializer().serializeToString(export_svg),
    // svg_base64: await DfImageEditorF.svgToBase64({ svg_el: export_svg }),
    svg_image_base64: handleSvgImageFile({ tab_el, svg_image_canvas: output_raster.canvas }),
  };
}

/**
 * 경로들의 계층 구조 트리를 구성
 * @param {Array} paths - 분석할 경로 배열
 * @returns {Array} - 계층 구조가 반영된 트리
 */
function buildPathTree(paths) {
  // 모든 경로 쌍에 대해 포함 관계 분석
  const containmentMap = new Map();

  for (const path of paths) {
    containmentMap.set(path, []);
  }

  // 경로 간 포함 관계 파악
  for (const path1 of paths) {
    for (const path2 of paths) {
      if (path1 !== path2 && isPathContaining(path1, path2)) {
        containmentMap.get(path1).push(path2);
      }
    }
  }

  // 직계 포함 관계만 남기기 (간접 포함 제거)
  for (const [container, contained] of containmentMap.entries()) {
    // 직접 포함관계만 필터링
    containmentMap.set(
      container,
      contained.filter((path) => {
        // path가 container에 직접 포함되는지 확인
        // 다른 경로들 중에 path를 포함하면서 container에 포함되는 경로가 없어야 함
        for (const otherContainer of paths) {
          if (
            otherContainer !== container &&
            otherContainer !== path &&
            isPathContaining(otherContainer, path) &&
            isPathContaining(container, otherContainer)
          ) {
            return false; // 간접 포함 관계
          }
        }
        return true; // 직접 포함 관계
      }),
    );
  }

  // 루트 노드 찾기 (다른 어떤 경로에도 포함되지 않는 경로)
  const rootPaths = paths.filter((path) => {
    for (const otherPath of paths) {
      if (path !== otherPath && isPathContaining(otherPath, path)) {
        return false; // 다른 경로에 포함됨
      }
    }
    return true; // 어떤 경로에도 포함되지 않음
  });

  // 트리 구조 구성
  function buildTree(path) {
    return {
      path,
      children: containmentMap.get(path).map((childPath) => buildTree(childPath)),
    };
  }

  return rootPaths.map((rootPath) => buildTree(rootPath));
}

/**
 * 한 경로가 다른 경로를 포함하는지 확인
 * @param {import('typescript').Path} container - 포함할 수 있는 경로
 * @param {import('typescript').Path} contained - 포함될 수 있는 경로
 * @returns {Boolean} - 포함 여부
 */
function isPathContaining(container, contained) {
  // contained의 모든 세그먼트 포인트가 container 내부에 있는지 확인
  return contained.segments.every((segment) => container.contains(segment.point));
}

/**
 * 짝수/홀수 레벨 규칙에 따라 경로 노드를 처리
 * @param {Object} node - 경로 트리 노드
 * @param {Number} level - 현재 계층 레벨 (0부터 시작)
 * @param {Object} raster - 색상 추출에 사용할 래스터
 * @param {Object} paper_scope - Paper.js 스코프
 * @param {import('pdf-lib').Color} transparentColor - 투명 색상 객체
 * @returns {import('typescript').Path} - 처리된 경로
 */
function processPathNode(node, level, raster, paper_scope, transparentColor) {
  const path = node.path;
  const children = node.children;

  if (children.length === 0) {
    // 자식이 없는 경우 (리프 노드)
    const clonedPath = path.clone();

    // 짝수 레벨은 채우고, 홀수 레벨은 비움
    if (level % 2 === 0) {
      clonedPath.fillColor = raster.getAverageColor(clonedPath);
    } else {
      clonedPath.fillColor = transparentColor;
    }

    clonedPath.strokeColor = transparentColor;
    return clonedPath;
  }

  // 자식이 있는 경우 (내부 노드)
  const resultPath = path.clone();

  // 짝수 레벨은 채우고, 홀수 레벨은 비움
  if (level % 2 === 0) {
    resultPath.fillColor = raster.getAverageColor(resultPath);
  } else {
    resultPath.fillColor = transparentColor;
  }

  resultPath.strokeColor = transparentColor;

  // 자식 노드들 처리
  const processedChildren = children.map((child) =>
    processPathNode(child, level + 1, raster, paper_scope, transparentColor),
  );

  // 중첩된 영역 처리를 위한 컴파운드 패스 생성
  const compoundPath = new paper_scope.CompoundPath();
  compoundPath.addChild(resultPath);

  for (const childPath of processedChildren) {
    // 컴파운드 패스인 경우
    if (childPath.children) {
      for (const p of childPath.children) {
        compoundPath.addChild(p.clone());
      }
    } else {
      compoundPath.addChild(childPath.clone());
    }
  }

  // 중첩 영역의 적절한 채우기/비우기 처리
  if (level % 2 === 0) {
    compoundPath.fillColor = raster.getAverageColor(path);
  } else {
    compoundPath.fillColor = transparentColor;
  }

  compoundPath.strokeColor = transparentColor;
  return compoundPath;
}

/**
 * 경로 트리 전체를 처리하여 최종 경로 배열 생성
 * @param {Array} pathTree - 경로 트리
 * @param {Object} raster - 색상 추출에 사용할 래스터
 * @param {Object} paper_scope - Paper.js 스코프
 * @param {import('pdf-lib').Color} transparentColor - 투명 색상 객체
 * @returns {Array} - 처리된 경로 배열
 */
function processPathTree(pathTree, raster, paper_scope, transparentColor) {
  // 각 루트 노드별로 처리
  return pathTree.map((rootNode) => {
    // 홀짝 규칙에 따라 경로 처리 (루트는 레벨 0)
    return processPathNode(rootNode, 0, raster, paper_scope, transparentColor);
  });
}
